import { api } from './axios'
import type {
  AcceptInvitationDTO,
  AcceptInvitationResponseDTO,
  ActivateUserResponseDto,
  CreateUserInvitationDTO,
  CreateUserInvitationResponse,
  GetInvitationInfoResponseDTO,
  GetUsersResponse,
  SuspendUserResponseDto,
  TransferPrimaryAdminDto,
  TransferPrimaryAdminResponseDto,
  UpdateProfileDto,
  UpdateProfileResponseDto,
  UserFilters,
} from './types'
import type { ResendUserInvitationResponse } from './types'

/**
 * Invite a user to a tenant
 * Calls POST /users/invitations
 */
export const createUserInvitation = async (
  invitation: CreateUserInvitationDTO,
): Promise<CreateUserInvitationResponse> => {
  const response = await api.post<CreateUserInvitationResponse>('/users/invitations', invitation)

  return response.data
}

/**
 * Resend a user invitation
 * Calls POST /users/{userId}/invitation
 */
export const resendUserInvitation = async (
  userId: string,
): Promise<ResendUserInvitationResponse> => {
  const response = await api.post<ResendUserInvitationResponse>(`/users/${userId}/invitation`)

  return response.data
}

/**
 * Delete a user invitation
 * Calls DELETE /users/{userId}/invitation
 */
export const deleteUserInvitation = async (userId: string): Promise<void> => {
  await api.delete(`/users/${userId}/invitation`)
}

/**
 * Fetch users with filters (search, status, role, emailConfirmed, pagination)
 * Calls GET /users
 */
export const getUsers = async (filters: UserFilters = {}): Promise<GetUsersResponse> => {
  const params = new URLSearchParams()

  if (filters.limit !== undefined) params.append('limit', filters.limit.toString())
  if (filters.offset !== undefined) params.append('offset', filters.offset.toString())
  if (filters.role) params.append('role', filters.role)
  if (filters.status) params.append('status', filters.status)
  if (filters.search) params.append('search', filters.search)
  if (filters.emailConfirmed !== undefined)
    params.append('emailConfirmed', String(filters.emailConfirmed))

  const queryString = params.toString()
  const url = queryString ? `/users?${queryString}` : '/users'

  const response = await api.get<GetUsersResponse>(url)

  return response.data
}

export const getInvitationInfo = async (
  tenantId: string,
  invitationCode: string,
): Promise<GetInvitationInfoResponseDTO> => {
  const response = await api.get<GetInvitationInfoResponseDTO>(
    `/public/invitations/${tenantId}/${invitationCode}`,
  )

  return response.data
}

/**
 * Accept a user invitation
 * Calls POST /public/invitations/{tenantId}/{invitationCode}/accept
 */
export const acceptInvitation = async (
  tenantId: string,
  invitationCode: string,
  data: AcceptInvitationDTO,
): Promise<AcceptInvitationResponseDTO> => {
  const response = await api.post<AcceptInvitationResponseDTO>(
    `/public/invitations/${tenantId}/${invitationCode}/accept`,
    data,
  )

  return response.data
}

/**
 * Suspend a user
 * Calls PATCH /users/{userId}/suspend
 */
export const suspendUser = async (userId: string): Promise<SuspendUserResponseDto> => {
  const response = await api.patch<SuspendUserResponseDto>(`/users/${userId}/suspend`)

  return response.data
}

/**
 * Activate a user
 * Calls PATCH /users/{userId}/activate
 */
export const activateUser = async (userId: string): Promise<ActivateUserResponseDto> => {
  const response = await api.patch<ActivateUserResponseDto>(`/users/${userId}/activate`)

  return response.data
}

/**
 * Update current user's profile
 * Calls PATCH /users/me/profile
 */
export const updateMyProfile = async (data: {
  firstName: string
  lastName: string
  phoneNumber: string
}): Promise<any> => {
  const response = await api.patch('/users/me/profile', data)

  return response.data
}

export const updateUserProfile = async (
  userId: string,
  data: UpdateProfileDto,
): Promise<UpdateProfileResponseDto> => {
  const response = await api.patch<UpdateProfileResponseDto>(`/users/${userId}/profile`, data)

  return response.data
}

/**
 * Transfer primary admin role
 * Calls POST /users/admin/transfer
 */
export const transferPrimaryAdmin = async (
  data: TransferPrimaryAdminDto,
): Promise<TransferPrimaryAdminResponseDto> => {
  const response = await api.post<TransferPrimaryAdminResponseDto>('/users/admin/transfer', data)

  return response.data
}
